import { SdkMessageTypes } from '../constants';
import { HostedWebContentSdkService } from '../hosted-web-content-sdk';
import { ILogService, IPageService, ITelemetryService, TelemetryScenarioName } from '../interfaces';
import { Themes, UserSettingTypes } from '../messages.interface';
import { BaseMessageHandler } from './base-message-handler';

type MetaOsEventWithPossibleData = { data?: any };
type OnMetaOsEventCallback = (event: MetaOsEventWithPossibleData) => void;
type OnRegisterForMetaOsEvent = (onEvent: OnMetaOsEventCallback) => void;

export class RegisterHandlerMessageHandler extends BaseMessageHandler {
  private registeredHandlers: Map<string, () => void> = new Map<string, () => void>();
  public userSettings: UserSettingTypes[] = [];

  constructor(
    protected appSdkHost: HostedWebContentSdkService,
    pageService?: IPageService,
    logService?: ILogService,
    telemetryService?: ITelemetryService
  ) {
    super(appSdkHost, telemetryService);

    this.registerHandlerImpl(
      'themeChange',
      (onEvent) => (appSdkHost.onThemeChanged = (newTheme: Themes) => onEvent({ data: [newTheme] })),
      'eventDispatch.themeChange'
    );

    this.registerHandlerImpl(
      'userSettingsChange',
      (onEvent) =>
        (appSdkHost.onUserSettingsChanged = (userSetting: UserSettingTypes, value: string) =>
          this.userSettings.includes(userSetting) && onEvent({ data: [userSetting, value] })),
      'eventDispatch.userSettingsChange'
    );

    if (logService) {
      logService.clearListeners();
      this.registerHandlerImpl(
        'log.request',
        (onEvent) => logService.addEventListener('log.request', onEvent),
        'eventDispatch.log.request'
      );
    }

    if (pageService) {
      pageService.clearListeners();
      this.registerHandlerImpl(
        'fullScreenChange',
        (onEvent) => pageService.addEventListener('fullScreenChange', onEvent),
        'eventDispatch.fullScreenChange'
      );

      if (pageService.appButtonService) {
        const appButtonService = pageService.appButtonService;
        appButtonService.clearListeners();
        this.registerHandlerImpl(
          'appButtonClick',
          (onEvent) => appButtonService.addEventListener('click', onEvent),
          'eventDispatch.appButtonClick'
        );
        this.registerHandlerImpl(
          'appButtonHoverEnter',
          (onEvent) => appButtonService.addEventListener('hoverEnter', onEvent),
          'eventDispatch.appButtonHoverEnter'
        );
        this.registerHandlerImpl(
          'appButtonHoverLeave',
          (onEvent) => appButtonService.addEventListener('hoverLeave', onEvent),
          'eventDispatch.appButtonHoverLeave'
        );
      }

      if (pageService.configService) {
        const configService = pageService.configService;
        configService.clearListeners();
        this.registerHandlerImpl(
          'changeSettings',
          (onEvent) => configService.addEventListener('changeSettings', onEvent),
          'eventDispatch.changeSettings'
        );
        configService.addEventListener('settings.save', (event: MetaOsEventWithPossibleData) => {
          this.logEventDispatchScenario('eventDispatch.settings.save');
          this.appSdkHost.sendRequestMessage('settings.save', event.data ? [event.data] : []);
        });
        configService.addEventListener('settings.remove', (event: MetaOsEventWithPossibleData) => {
          this.logEventDispatchScenario('eventDispatch.settings.remove');
          this.appSdkHost.sendRequestMessage('settings.remove', event.data ? [event.data] : []);
        });
      }

      if (pageService.backStackService) {
        const backStackService = pageService.backStackService;
        backStackService.clearListeners();
        this.registerHandlerImpl(
          'backButton',
          (onEvent) => backStackService.addEventListener('backButton', onEvent),
          'eventDispatch.backButtonPress',
          'backButtonPress'
        );
      }
    }
  }
  public initHandlers(): void {
    this.registerHandler(SdkMessageTypes.registerHandler, (_, type: string, userSettings: UserSettingTypes[]) => {
      if (userSettings) {
        this.userSettings = userSettings;
      }
      if (this.registeredHandlers.has(type)) {
        this.registeredHandlers.get(type)!();
      }
    });
  }

  private registerHandlerImpl(
    type: string,
    handlerFactory: OnRegisterForMetaOsEvent,
    telemetryScenarioName: TelemetryScenarioName,
    eventRaiseType?: string
  ) {
    if (this.registeredHandlers.has(type)) {
      throw new Error('handler already registered');
    }

    this.registeredHandlers.set(type, () =>
      handlerFactory((event: MetaOsEventWithPossibleData) => {
        this.logEventDispatchScenario(telemetryScenarioName);

        if (!Array.isArray(event.data)) event.data = [event.data];
        this.appSdkHost.sendRequestMessage(eventRaiseType ?? type, event.data ? event.data : []);
      })
    );
  }

  private logEventDispatchScenario(telemetryScenarioName: TelemetryScenarioName) {
    const scenario = this.telemetryService?.createScenario(telemetryScenarioName);
    scenario?.stop();
  }
}
